/**
 * Rasterise the report's live charts to high-resolution PNG data URLs.
 *
 * The PDF export (option 1B) is a vector `@react-pdf/renderer` document for the
 * chrome + KPI cards, with each chart embedded as a pixel-faithful image. The
 * charts on screen are Recharts `<svg>` elements inside a card; this module
 * captures each card to a PNG the PDF can embed.
 *
 * Capture is delegated to `modern-screenshot`'s `domToPng` (a maintained
 * `html-to-image` successor) rather than a hand-rolled SVG serialiser. That
 * matters here because:
 *
 *  - **Shadow DOM + computed styles.** Sections render inside a shadow root and
 *    style with Emotion/Chakra, so the chart's paint lives in stylesheets, not
 *    inline on the SVG nodes. `domToPng` clones the node and inlines the
 *    *computed* styles (and web fonts) for us, where a raw `XMLSerializer` copy
 *    would drop them and render blank — which is exactly why the earlier
 *    hand-rolled approach produced empty charts.
 *  - **Recharts internals.** Recharts uses constructs (e.g. `foreignObject`,
 *    CSS-driven fills) that a bare SVG-to-image round-trip fails to load;
 *    `domToPng` handles them.
 *
 * We capture the whole chart **card** (not just the `<svg>`) so the card's title
 * and Avg/Total pills are included in the image, keeping the PDF a faithful
 * replica of the on-screen chart.
 */
import { domToPng } from "modern-screenshot"
import { CAPTURE_ATTR } from "./chartHeaderTags"

/**
 * A rasterised chart ready to embed in the PDF.
 */
export interface ChartImage {
  /** PNG data URL of the chart at export resolution. */
  dataUrl: string
  /** Natural (CSS) width of the source card, for the PDF's aspect ratio. */
  width: number
  /** Natural (CSS) height of the source card, for the PDF's aspect ratio. */
  height: number
  /** Chart card title (from `data-chart-title`), for the PDF section heading. */
  title: string
  /** Document section the chart belongs to (from `data-chart-section`). */
  section: string
}

/**
 * Marks a chart card in the report DOM so the exporter can find it, and carries
 * the metadata the PDF needs. Set on the outer card `Box` of each chart.
 */
export const CHART_CARD_ATTR = "data-report-chart"
/** Attribute carrying the chart's title (the card heading). */
export const CHART_TITLE_ATTR = "data-chart-title"
/** Attribute carrying the chart's document section (e.g. "Profile"). */
export const CHART_SECTION_ATTR = "data-chart-section"

/**
 * Device-pixel multiplier for the captured charts. 3× keeps lines and axis text
 * sharp when the PDF is viewed at 100% or printed.
 */
const RASTER_SCALE = 3

/**
 * The PDF's printable content width in points (A4 width 595.28pt − 2×28pt page
 * padding). A full-width (lone) chart is placed at this width; these values
 * mirror {@link ReportPdfDocument}'s layout constants and MUST stay in sync with
 * them.
 */
const PDF_CONTENT_WIDTH_PT = 595.28 - 28 * 2

/**
 * Why each chart is captured at the width it will occupy in the PDF.
 *
 * The PDF places every chart on its own full-width row (never side by side; see
 * {@link ReportPdfDocument}'s `toChartRows`), so every chart is captured at the
 * full content width. Chart text (axis ticks, titles, Avg/Total pills) is drawn
 * at a fixed CSS pixel size, and the PDF scales each captured image to its cell
 * width, so a chart's on-page text size is `fontPx × (cellWidthPt /
 * captureWidthPx)`. That ratio is 1 (uniform font size) when the capture width
 * equals the placement width. A full row also gives the header room for the
 * heading and tags on one line.
 *
 * Recharts only re-lays-out at a new width if a live, React-rendered chart is
 * actually resized (a detached clone keeps its old geometry), so we set the
 * width on each live card, let it reflow, then capture.
 */
const FULL_WIDTH_CAPTURE_PX = Math.round(PDF_CONTENT_WIDTH_PT)

/**
 * Target plot-area shape during capture, as plot height ÷ capture width.
 *
 * On screen the plot area is a fixed 260px tall whatever the card's width. During
 * capture we override that with a height proportional to the capture width, so
 * every chart in the PDF has the same shape. At the full 485px width, 0.4 gives a
 * ~194px plot, a landscape shape that keeps full-width rows from running long
 * (several charts per page). Tune to taste.
 */
const CHART_PLOT_ASPECT = 0.4

/**
 * Class names of transient Recharts overlays that should be excluded from the
 * captured image: the hover tooltip wrapper and the hover cursor highlight. If
 * the user is hovering a chart at export time these would otherwise be baked in.
 */
const EXCLUDED_OVERLAY_CLASSES = ["recharts-tooltip-wrapper", "recharts-tooltip-cursor"] as const

/**
 * `modern-screenshot` node filter: return `false` to drop a node from the
 * capture. Drops any element carrying an excluded overlay class (the open
 * tooltip / hover cursor); keeps everything else, including non-element nodes.
 */
function excludeChartOverlays(node: Node): boolean {
  if (!(node instanceof Element)) return true
  return !EXCLUDED_OVERLAY_CLASSES.some((cls) => node.classList.contains(cls))
}

/**
 * Inline styles that pin a single chart card to an exact pixel width during
 * capture, overriding its responsive grid track so Recharts re-lays-out at that
 * width. `flex`/`gridColumn` are neutralised so a grid/flex parent can't stretch
 * the card back to the track width. Restored after capture.
 */
function cardCaptureStyle(widthPx: number): Partial<CSSStyleDeclaration> {
  return {
    width: `${widthPx}px`,
    minWidth: `${widthPx}px`,
    maxWidth: `${widthPx}px`,
    flex: "none",
    gridColumn: "auto",
  }
}

/**
 * Inline styles that give a chart's plot area a deterministic height during
 * capture, so its shape no longer depends on the fixed on-screen 260px. Applied
 * to the `Chart.Root` box (the parent of `.recharts-responsive-container`), whose
 * `ResponsiveContainer` follows this height after a reflow.
 */
function chartPlotCaptureStyle(heightPx: number): Partial<CSSStyleDeclaration> {
  return {
    height: `${heightPx}px`,
    minHeight: `${heightPx}px`,
    maxHeight: `${heightPx}px`,
  }
}

/**
 * The `Chart.Root` box for a card: the element wrapping the Recharts responsive
 * container, whose height controls the plot area. Returns null for a card whose
 * chart isn't a responsive-container chart (defensive; all report charts are).
 */
function chartPlotBox(card: HTMLElement): HTMLElement | null {
  const responsive = card.querySelector<HTMLElement>(".recharts-responsive-container")
  return responsive?.parentElement ?? null
}

/** Resolve after two animation frames (or a short timeout when rAF is absent),
 * giving Recharts' ResizeObserver-driven `ResponsiveContainer` time to observe
 * the new container width and re-render each chart's SVG at that width. A single
 * frame can land before the observer fires; two is reliable in practice. */
function waitForReflow(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== "function") {
      setTimeout(resolve, 32)
      return
    }
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
}

/**
 * Apply `styles` to `element`'s inline style, returning a function that restores
 * the exact previous inline values (so the container is left byte-for-byte as it
 * was, not merely cleared).
 */
function applyInlineStyles(element: HTMLElement, styles: Partial<CSSStyleDeclaration>): () => void {
  const previous = new Map<string, string>()
  for (const key of Object.keys(styles)) {
    previous.set(key, element.style[key as any])
    element.style[key as any] = styles[key as any] as string
  }
  return () => {
    for (const [key, value] of previous) {
      element.style[key as any] = value
    }
  }
}

/**
 * Rasterise every chart card inside `container` to a PNG, in document order.
 *
 * Cards are found by the {@link CHART_CARD_ATTR} attribute (the same tags the
 * on-screen report sets). Each card that contains a chart `<svg>` is captured;
 * cards without one (e.g. a "No data available" placeholder) are skipped so the
 * PDF never shows a blank frame. A card that fails to capture is skipped rather
 * than aborting the whole export, so one bad graph never blocks the PDF.
 *
 * ## Why each card is captured at full width
 *
 * The PDF places every chart on its own full-width row, so every card is
 * captured at {@link FULL_WIDTH_CAPTURE_PX} (see the note on that constant).
 * Capture width and placement width must stay in sync with `toChartRows` /
 * `ChartRow` in {@link ReportPdfDocument}.
 *
 * Recharts only re-lays-out when a live, React-rendered chart is resized, so the
 * width is set on each live card, we wait for reflow, then capture. The card's
 * inline styles are restored afterwards. The caller is expected to cover the
 * report while this runs (the resize is otherwise visible); see the report's
 * `generating`-driven overlay.
 *
 * @param container The report surface node whose charts should be captured.
 * @returns One {@link ChartImage} per successfully captured chart, in order.
 */
export async function rasterizeCharts(container: HTMLElement): Promise<ChartImage[]> {
  const images: ChartImage[] = []

  // Only cards that actually hold a rendered chart are captured; a "No data"
  // placeholder card has no <svg> and is skipped so the PDF never shows a blank.
  const cards = Array.from(container.querySelectorAll<HTMLElement>(`[${CHART_CARD_ATTR}]`)).filter((card) =>
    card.querySelector("svg"),
  )

  // Pin every card to its target width and every plot area to a proportional
  // height (a fixed shape) up front, then reflow once so Recharts re-lays-out
  // all charts together before any capture. Styles are restored in `finally` so
  // the on-screen report is left exactly as it was.
  const restores: Array<() => void> = []
  for (const card of cards) {
    const targetWidth = FULL_WIDTH_CAPTURE_PX
    restores.push(applyInlineStyles(card, cardCaptureStyle(targetWidth)))
    // Flag the card so its header tags and chart text switch to compact export
    // sizing (see chartHeaderTags.ts); removed again with the other restores.
    card.setAttribute(CAPTURE_ATTR, "")
    restores.push(() => card.removeAttribute(CAPTURE_ATTR))
    const plotBox = chartPlotBox(card)
    if (plotBox) {
      restores.push(applyInlineStyles(plotBox, chartPlotCaptureStyle(Math.round(targetWidth * CHART_PLOT_ASPECT))))
    }
  }
  try {
    await waitForReflow()

    for (const card of cards) {
      const rect = card.getBoundingClientRect()
      const width = rect.width
      const height = rect.height
      if (width < 40 || height < 40) continue

      const title = card.getAttribute(CHART_TITLE_ATTR) ?? ""
      const section = card.getAttribute(CHART_SECTION_ATTR) ?? ""

      try {
        const dataUrl = await domToPng(card, {
          scale: RASTER_SCALE,
          // The report cards float on a muted canvas; paint the card's own white
          // so the captured image reads on the PDF page rather than transparent.
          backgroundColor: "#ffffff",
          // Exclude any open Recharts hover tooltip from the capture — if the
          // user is hovering a chart when they export, the tooltip div would
          // otherwise be baked into the image.
          filter: excludeChartOverlays,
        })
        // width == the card's target cell width, so cellWidthPt / captureWidthPx
        // is ~1 for every chart and the exported font size is uniform.
        images.push({ dataUrl, width, height, title, section })
      } catch {
        // Skip a chart that can't be captured rather than failing the export.
        continue
      }
    }
  } finally {
    for (const restore of restores) restore()
  }

  return images
}
